// @ts-check
import { values, keyBy, forEach, groupBy, map } from "lodash";

import { reportError } from "src/services/errorReporting";

import {
  getForecastColumns,
  getForecastMacroAllocations,
  setGroupingsWithActualConstraints,
  setGroupingsWithDefaultConstraints,
  setGroupingsWithForecastCells,
  setGroupingsWithForecastDefaultCells,
  setCalculatedSumTotals,
  setGroupingsTotalCost,
  setNotifications,
  pruneEmptyLineItems,
  pruneEmptyGroupings,
  buildGroupingLookup,
  buildCellLookup,
  buildLineItemConstraintLookups,
  hashGrouping,
  buildGroupLookup,
  setGroupingsBudget,
  checkForForecastPending,
} from "../util";

import buildGroupings from "./buildGroupings/byDemand";
import setGroupingsWithLineItems from "./setLineItems/supplyLineItems";

const buildGroupedByDemand = ({
  rootGroup,
  data,
  hideHiddenTeams,
  showObjectives,
  groupTypes,
  shouldHash,
  isBudgetEnabled,
  overBudgetLimit,
  underBudgetLimit,
}) => {
  // Handle null/undefined data gracefully
  if (!data) {
    const error = new Error(
      "buildGroupedByDemand: data is null or undefined. Forecast query data may not have loaded yet."
    );
    reportError(error, {
      rootGroupId: rootGroup?.id,
      rootGroupName: rootGroup?.name,
      viewMode: "GROUPED_BY_DEMAND",
    });

    // Return safe default structure to prevent further errors
    return {
      isPendingSubmit: false,
      columns: [],
      groupings: [],
      lookups: {
        allocationProjectLookup: {},
        columnLookup: {},
        groupingLookup: {},
        lineItemLookup: {},
        cellLookup: {},
        constraintCellLookup: {},
      },
    };
  }

  // Ensure allocationProjects exists, default to empty array
  const allocationProjects = data.allocationProjects || [];
  const columns = getForecastColumns(allocationProjects);
  const allocationProjectLookup = keyBy(
    map(columns, (c) => c.allocationProject),
    "id"
  );
  const groupLookup = buildGroupLookup({
    macroAllocationGroups: data.macroAllocationGroups || [],
    descendantSupplyGroups: data.descendantSupplyGroups || [],
    roleAreaGroups: data.roleAreaGroups || [],
    relatedSupplyGroups: data.relatedSupplyGroups || [],
    descendantTargetGroups: data.descendantTargetGroups || [],
    relatedBudgetTargetGroups: data.relatedBudgetTargetGroups || [],
    groupTypes,
  });
  const constraints = groupBy(data.constraints || [], "groupId");

  const macroAllocations = getForecastMacroAllocations({
    macroAllocations: data.macroAllocations || [],
    allocationProjectLookup,
    groupedByDemand: true,
  });

  const groupings = buildGroupings({
    rootGroup,
    groupLookup,
    hideHiddenTeams,
    showObjectives,
    groupTypes,
    columnCount: columns.length,
    isBudgetEnabled,
    allocationProjectLookup,
  });

  setGroupingsWithLineItems({
    groupings,
    macroAllocations,
    columnCount: columns.length,
    groupTypes,
    canRename: rootGroup.isSource,
    isBudgetEnabled,
    groupLookup,
  });

  setGroupingsWithForecastCells({
    groupings,
    macroAllocations,
    columns,
    groupedByDemand: true,
    rootGroup,
  });
  setGroupingsWithForecastDefaultCells(groupings, columns, true, rootGroup);

  if (isBudgetEnabled) {
    setGroupingsBudget({
      groupings,
      relatedSupplyGroups: data.relatedSupplyGroups || [],
      roleAreaGroups: data.roleAreaGroups || [],
      allocationProjectLookup,
    });
    setGroupingsTotalCost(groupings, overBudgetLimit, underBudgetLimit);
  }

  setCalculatedSumTotals(groupings, columns);
  setGroupingsWithActualConstraints(
    groupings,
    constraints,
    allocationProjectLookup,
    columns
  );
  setGroupingsWithDefaultConstraints(
    groupings,
    allocationProjectLookup,
    columns
  );
  setNotifications(groupings, columns);

  pruneEmptyLineItems(groupings);
  if (rootGroup.isSource) {
    pruneEmptyGroupings(groupings, isBudgetEnabled);
  }

  const columnLookup = keyBy(columns, "id");
  const groupingLookup = buildGroupingLookup(groupings, {});
  const { lineItemLookup, constraintCellLookup } =
    buildLineItemConstraintLookups(values(groupingLookup), {}, {});
  const cellLookup = buildCellLookup(values(lineItemLookup), {});
  const isPendingSubmit = checkForForecastPending(groupingLookup);

  // Hash groupings in case anything changed
  if (shouldHash) {
    // @ts-ignore
    forEach(groupings, hashGrouping);
  }

  return {
    isPendingSubmit,
    columns,
    groupings,
    // lookup objects: for when we need fast access to objects in the tree
    lookups: {
      allocationProjectLookup,
      columnLookup,
      groupLookup,
      groupingLookup,
      lineItemLookup,
      cellLookup,
      constraintCellLookup,
      macroAllocationLookup: macroAllocations,
    },
  };
};

export default buildGroupedByDemand;
